import type {GuidedApiCallParams, GuidedItemsV2, GuidedItemV2} from '../models/models';
import {makeLogger} from '../../../logger/logger';
import {
  type AmbigousSuggestion,
  type CoseContextLocale,
  SuggestionBase,
  type SuggestionsResultV2ResponseDto
} from '../models/cose';
import {
  GuidedGenericQuickLink,
  GuidedQuickLinkDetails,
} from '../models/quick-link';
import type {CoseGuidedV2Context} from '../../../models/cose-api/request/query-body-request.model';
import {makeUrlForQuery} from '../utils';
import type {GuidedV2BannerCta} from '../models/banner';

const {
  info,
  // debug,
  warn,
  err
} = makeLogger({
  pathToCurrentFile: 'components/guided2/middlewares/mapCoseToGuidedV2.ts',
});


// @ts-ignore
// noinspection OverlyComplexFunctionJS
export const mapSecItem: <
  T extends SuggestionBase = AmbigousSuggestion,
  R extends GuidedQuickLinkDetails = GuidedGenericQuickLink,
> (groupDetails: {
  groupId: string,
  searcher: string,
  context: CoseGuidedV2Context
}, item: T) => null | GuidedItemV2<R> = (groupDetails, item) => {
    try {

      let {
        groupId,
        searcher,
        context,
      } = groupDetails;

      //Reading country & language parameters
      let language = 'en';
      let country = 'us';
      if ('language' in context) {
        language = context.language.toLowerCase();
      } else {
        warn('quickLinkOpenUrl for item not found', {item});
      }
      if (context.type == 'cose/context/locale' && 'country' in context) {
        country = context.country.toLowerCase();
      }

      const localeSymbol = language + '_' + country.toUpperCase();

      //then parsing the quicklink

      let quickLinkOpenUrl: string;
      if ('openUrl' in item && item.openUrl) {
        quickLinkOpenUrl = item.openUrl as string;
      } else if ('quickLinkUrl' in item && item.quickLinkUrl) {
        quickLinkOpenUrl = item.quickLinkUrl as string;
      } else if ('userQuery' in item && item.userQuery) {
        quickLinkOpenUrl = makeUrlForQuery(country, language, item.userQuery as string);
      } else if ('keyword' in item && item.keyword) {
        quickLinkOpenUrl = makeUrlForQuery(country, language, item.keyword as string);
      }

      let quickLinkDescription: string = '';
      if ('description' in item && item.description) {
        quickLinkDescription = item.description as string;
      }else if (localeSymbol in item && 'description' in item[localeSymbol] && item[localeSymbol].description){
        quickLinkDescription = item[localeSymbol].description as string;
      }else if (localeSymbol in item && 'longDescription' in item[localeSymbol] && item[localeSymbol].longDescription){
        quickLinkDescription = item[localeSymbol].longDescription as string;
      }else if ('bannerReference' in item && item.bannerReference){
        quickLinkDescription = item.bannerReference as string;
      }

      let quickLinkTitle: string = '';
      if ('title' in item && item.title) {
        quickLinkTitle = item.title as string;
      } else if ('userQuery' in item && item.userQuery) {
        quickLinkTitle = item.userQuery as string;
      } else if ('keyword' in item && item.keyword) {
        quickLinkTitle = item.keyword as string;
      }else if (localeSymbol in item && 'title' in item[localeSymbol] && item[localeSymbol].title){
        quickLinkTitle = item[localeSymbol].title as string;
      }

      let imageUrl = '';
      if ('imageUrl' in item && item.imageUrl) {
        imageUrl = item.imageUrl as string;
      }else if ('image' in item && item.image) {
        imageUrl = item.image as string;
      }

      const template = 'default';

      //Special handling for promoted guided banners
      if (groupId === 'promoted-quicklinks' && 'type' in item && item?.type == 'banner') {
        groupId = 'promoted-guided-banners';
      }


      if (groupId === 'promoted-guided-banners') {
        return {
          searcher: searcher,
          category: groupId,
          template,
          score: item._score,
          details: {
            bannerTitle: quickLinkTitle,
            bannerDescription: quickLinkDescription,
            bannerOpenUrl: quickLinkOpenUrl,
            bannerButtonText: 'buttonText' in item && item.buttonText ? item.buttonText as string : 'Learn more',
            bannerButtonTarget: '_self',
          } as GuidedV2BannerCta
        };
      }


      const result: GuidedItemV2<GuidedGenericQuickLink> = {
        searcher: searcher,
        category: groupId,
        template,
        score: item._score,
        details: {
          _id: item._id,
          quickLinkOpenUrl: quickLinkOpenUrl,
          quickLinkTitle: quickLinkTitle,
          quickLinkImage: imageUrl,
          quickLinkDescription,
        }
      };
      return result;

    } catch (e) {
      err('Failed to map Cose 2 Guided item:', {groupDetails, item});
      return null;
    }

};


export const mapSecToGuidedV2 = async (
  details: GuidedApiCallParams,
  next
) => {

    // info('Enter mapCoseToGuidedV2 middleware', {details});
    try {
      const data = await next(details) as SuggestionsResultV2ResponseDto<
      AmbigousSuggestion,
      CoseContextLocale
    >;

      const returnItems: GuidedItemsV2 = data.groups.map(group => {
          return group.documents.map(item => mapSecItem({
            searcher: data.searcher,
            groupId: group.id,
            context: data.context,
          }, item
          ));
      }).flat().filter(item => item != null);

      return returnItems;

    } catch (e) {
      err('Exception happened', {e, details});
      info('Returning empty result list, due to exception');
      return [];
    }
};
